import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactElement } from "react";

/**
 * Loading, failed and empty are three states, not one.
 *
 * `query.data?.length ? … : <p>Nothing here</p>` collapses all three into the
 * most confident one. A request that 500s renders as an authoritative claim
 * that the thing does not exist — and the two places that matters most here are
 * an attempt's artifacts, where "no diagnosis was produced" invites re-running
 * an agent that already ran, and the audit trail, where an empty append-only
 * log is a security claim rather than a blank panel.
 *
 * Returns the element to render *instead of* the list, or `null` when there is
 * genuinely a list to show:
 *
 *     {listState(caps, "confidence checks", "No confidence checks recorded.") ?? (
 *       <ul>…</ul>
 *     )}
 *
 * `what` names the thing in prose, lower case, plural — it is interpolated into
 * both the loading and the failure line.
 */
export function listState<T>(
  query: UseQueryResult<T[], Error>,
  what: string,
  empty: string,
): ReactElement | null {
  if (query.isPending) return <p className="muted">Loading {what}…</p>;
  if (query.error) {
    return (
      <p className="error">
        Could not load {what}: {query.error.message}
      </p>
    );
  }
  if (!query.data.length) return <p className="muted">{empty}</p>;
  return null;
}
